/**
 * Zustand store for task filter and sort state.
 * Persists to localStorage so state survives page refreshes.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TaskStatus } from "@/types";

/**
 * Selected deadline week for the task board's week selector.
 *  - `all`        → no deadline constraint (the "Kaikki" chip)
 *  - `week`       → a single ISO 8601 week (Helsinki local time)
 *  - `noDeadline` → only tasks with no due date
 */
export type DeadlineWeekValue =
  | { kind: "all" }
  | { kind: "week"; year: number; week: number }
  | { kind: "noDeadline" };

export type SortBy = "deadline" | "taskName" | "ppCode";

export type SortDirection = "asc" | "desc";

export type ViewFilter = "Kaikkien" | "Minun";

interface TaskFilterState {
  // Existing filters
  search: string;
  projectFilter: string[];
  viewFilter: ViewFilter;
  showOthersMemoLists: boolean;

  // Membership filter: narrows the effective project scope to projects where
  // the current user has a role in project_roles. Cascades down through
  // plot/procurement-package/section via the existing logic.
  //
  // Always `true`. Access management scopes lists server-side, so a
  // "show other projects" affordance is meaningless and misleading — the
  // user-facing toggle was removed. The field is kept because the cascading
  // scope logic still reads it; it is no longer persisted and never flips.
  onlyMyProjects: boolean;

  // Entity filters
  plotFilter: string[];
  locationFilter: string[];
  assignedToFilter: string[];
  // Deadline-week selector state. Defaults to "all" (the "Kaikki" chip).
  deadlineWeek: DeadlineWeekValue;
  procurementPackageFilter: string[];
  workSectionFilter: string[];
  taskTypeFilter: string[];
  statusFilter: TaskStatus[];

  // Sorting
  sortBy: SortBy;
  sortDirection: SortDirection;

  // Actions
  setSearch: (v: string) => void;
  setProjectFilter: (v: string[]) => void;
  setViewFilter: (v: ViewFilter) => void;
  setShowOthersMemoLists: (v: boolean) => void;
  setOnlyMyProjects: (v: boolean) => void;
  setPlotFilter: (v: string[]) => void;
  setLocationFilter: (v: string[]) => void;
  setAssignedToFilter: (v: string[]) => void;
  setDeadlineWeek: (v: DeadlineWeekValue) => void;
  setProcurementPackageFilter: (v: string[]) => void;
  setWorkSectionFilter: (v: string[]) => void;
  setTaskTypeFilter: (v: string[]) => void;
  setStatusFilter: (v: TaskStatus[]) => void;
  setSortBy: (v: SortBy) => void;
  setSortDirection: (v: SortDirection) => void;
  toggleSortDirection: () => void;
  resetFilters: () => void;
}

const initialState = {
  search: "",
  projectFilter: [] as string[],
  viewFilter: "Minun" as ViewFilter,
  showOthersMemoLists: false,
  onlyMyProjects: true,
  plotFilter: [] as string[],
  locationFilter: [] as string[],
  assignedToFilter: [] as string[],
  deadlineWeek: { kind: "all" } as DeadlineWeekValue,
  procurementPackageFilter: [] as string[],
  workSectionFilter: [] as string[],
  taskTypeFilter: [] as string[],
  statusFilter: [] as TaskStatus[],
  sortBy: "deadline" as SortBy,
  sortDirection: "asc" as SortDirection,
};

export const useTaskFilterStore = create<TaskFilterState>()(
  persist(
    (set) => ({
      ...initialState,

      setSearch: (v) => set({ search: v }),
      setProjectFilter: (v) => set({ projectFilter: v }),
      setViewFilter: (v) => set({ viewFilter: v }),
      setShowOthersMemoLists: (v) => set({ showOthersMemoLists: v }),
      setOnlyMyProjects: (v) => set({ onlyMyProjects: v }),
      setPlotFilter: (v) => set({ plotFilter: v }),
      setLocationFilter: (v) => set({ locationFilter: v }),
      setAssignedToFilter: (v) => set({ assignedToFilter: v }),
      setDeadlineWeek: (v) => set({ deadlineWeek: v }),
      setProcurementPackageFilter: (v) => set({ procurementPackageFilter: v }),
      setWorkSectionFilter: (v) => set({ workSectionFilter: v }),
      setTaskTypeFilter: (v) => set({ taskTypeFilter: v }),
      setStatusFilter: (v) => set({ statusFilter: v }),
      setSortBy: (v) => set({ sortBy: v }),
      setSortDirection: (v) => set({ sortDirection: v }),
      toggleSortDirection: () =>
        set((state) => ({
          sortDirection: state.sortDirection === "asc" ? "desc" : "asc",
        })),
      resetFilters: () => set(initialState),
    }),
    {
      name: "task-filter-store",
      // Bumped when the persisted shape changes. v1 dropped the legacy
      // `deadlineFilter` string-token array in favour of `deadlineWeek`; v2
      // removed the first-visit auto-select-current-week behaviour. v3 added
      // the `locationFilter` multi-select (Sijainti); v4 dropped the
      // `onlyMyProjects` toggle so it always defaults to `true`.
      version: 4,
      migrate: (persistedState, version) => {
        const state = (persistedState ?? {}) as Record<string, unknown>;
        if (version < 1) {
          // Drop the legacy preset-token array and seed the week selector.
          delete state.deadlineFilter;
          state.deadlineWeek = { kind: "all" };
        }
        if (version < 2) {
          // The selector now simply defaults to "all". Drop the obsolete
          // init flag and reset any auto-seeded week so the new default
          // takes effect for returning users.
          delete state.hasInitializedDeadlineWeek;
          state.deadlineWeek = { kind: "all" };
        }
        if (version < 3) {
          state.locationFilter = [];
        }
        if (version < 4) {
          // The "show other projects" toggle was removed. Drop any persisted
          // value so returning users who had it off get reset to the default
          // (`true` — only their own projects).
          delete state.onlyMyProjects;
        }
        return state as unknown as TaskFilterState;
      },
      // Only persist filter/sort values, not actions
      partialize: (state) => ({
        search: state.search,
        projectFilter: state.projectFilter,
        viewFilter: state.viewFilter,
        plotFilter: state.plotFilter,
        locationFilter: state.locationFilter,
        assignedToFilter: state.assignedToFilter,
        deadlineWeek: state.deadlineWeek,
        procurementPackageFilter: state.procurementPackageFilter,
        workSectionFilter: state.workSectionFilter,
        taskTypeFilter: state.taskTypeFilter,
        statusFilter: state.statusFilter,
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
      }),
    },
  ),
);
